<template>
  <b-modal  v-model="isOpen"
            size="lg"
            centered
            @ok="handleSave"
            @cancel="handleCancel">
    <template #title>
      <div class="modal-title-wrapper">
        <Grid3x3 :size="20" class="title-icon" />
        <h2 class="modal-title">Grid-Ansicht konfigurieren</h2>
      </div>
    </template>

    <template #default>
      <p class="modal-description">
        Passen Sie das Layout, die Anzeige und das Verhalten Ihrer KPI-Karten an
      </p>

      <div class="config-content">
        <!-- Layout Section -->
        <div class="config-section">
          <div class="section-header">
            <Layers :size="16" class="section-icon" />
            <h3 class="section-title">Layout</h3>
          </div>

          <div class="form-group">
            <label class="form-label">Grid-Dimensionen</label>
            <div class="grid-inputs">
              <div class="input-wrapper">
                <label for="grid-rows" class="input-label">Zeilen</label>
                <b-form-input id="grid-rows"
                              v-model.number="config.rows"
                              type="number"
                              min="1"
                              max="10"
                              size="sm" />
              </div>
              <div class="input-wrapper">
                <label for="grid-cols" class="input-label">Spalten</label>
                <b-form-input id="grid-cols"
                              v-model.number="config.columns"
                              type="number"
                              min="1"
                              max="6"
                              size="sm" />
              </div>
            </div>
            <p class="help-text">
              Aktuell: {{ config.rows }} × {{ config.columns }} = {{ totalCards }} KPI-Karten
            </p>
          </div>

          <b-form-group label="Darstellungsdichte" label-for="density">
            <b-form-select  id="density"
                            v-model="config.density"
                            :options="densityOptions"
                            size="sm" />
          </b-form-group>

          <b-form-group label="Karten-Design" label-for="card-design">
            <b-form-select  id="card-design"
                            v-model="config.cardDesign"
                            :options="cardDesignOptions"
                            size="sm">
              <template #first>
                <b-form-select-option :value="null" disabled>
                  Design auswählen
                </b-form-select-option>
              </template>
            </b-form-select>
            <template #description>
              <small class="design-description">
                {{ getDesignDescription(config.cardDesign) }}
              </small>
            </template>
          </b-form-group>
        </div>

        <hr class="section-divider">

        <!-- Sorting Section -->
        <div class="config-section">
          <div class="section-header">
            <ArrowUpNarrowWide :size="16" class="section-icon" />
            <h3 class="section-title">Sortierung</h3>
          </div>

          <div class="grid-inputs">
            <b-form-group label="Sortieren nach" label-for="sort-by" class="mb-0">
              <b-form-select  id="sort-by"
                              v-model="config.sortBy"
                              :options="sortByOptions"
                              size="sm" />
            </b-form-group>

            <b-form-group label="Reihenfolge" label-for="sort-order" class="mb-0">
              <b-form-select  id="sort-order"
                              v-model="config.sortOrder"
                              :options="sortOrderOptions"
                              size="sm" />
            </b-form-group>
          </div>
        </div>

        <hr class="section-divider">

        <!-- Display Options Section -->
        <div class="config-section">
          <div class="section-header">
            <Eye :size="16" class="section-icon" />
            <h3 class="section-title">Anzeige-Optionen</h3>
          </div>

          <div class="checkbox-group">
            <b-form-checkbox v-model="config.showBadges">
              Badges anzeigen (Land, Welle, Status)
            </b-form-checkbox>
            <b-form-checkbox v-model="config.showTrend">
              Trend-Anzeige einblenden
            </b-form-checkbox>
            <b-form-checkbox v-model="config.showPreviousValue">
              Vorwellen-Wert anzeigen
            </b-form-checkbox>
            <b-form-checkbox v-model="config.showTarget">
              Zielwert anzeigen
            </b-form-checkbox>
          </div>
        </div>

        <hr class="section-divider">

        <!-- Interaction Section -->
        <div class="config-section">
          <div class="section-header">
            <Activity :size="16" class="section-icon" />
            <h3 class="section-title">Interaktion</h3>
          </div>

          <div class="switch-item">
            <div class="switch-info">
              <label class="switch-label" for="hover-effects">
                Hover-Effekte aktivieren
              </label>
              <p class="switch-description">
                Animierte Hover-Effekte und Action-Buttons bei Mausübergang
              </p>
            </div>
            <b-form-checkbox  id="hover-effects"
                              v-model="config.hoverEffects"
                              switch />
          </div>

          <div class="switch-item">
            <div class="switch-info">
              <label class="switch-label" for="click-to-details">
                Click-to-Details
              </label>
              <p class="switch-description">
                Bei Klick auf Karte zur Detail-Ansicht navigieren
              </p>
            </div>
            <b-form-checkbox  id="click-to-details"
                              v-model="config.clickToDetails"
                              switch />
          </div>
        </div>
      </div>
    </template>

    <template #footer="{ ok, cancel }">
      <b-button variant="outline-secondary" @click="cancel()">
        <X :size="16" class="me-2" />
        Abbrechen
      </b-button>
      <b-button variant="primary" @click="ok()">
        <Save :size="16" class="me-2" />
        Speichern
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  Grid3x3,
  Layers,
  ArrowUpNarrowWide,
  Eye,
  Activity,
  X,
  Save
} from 'lucide-vue-next';

export default {
  name: 'GridConfigDialog',

  components: {
    Grid3x3,
    Layers,
    ArrowUpNarrowWide,
    Eye,
    Activity,
    X,
    Save
  },

  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    initialConfig: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ['update:modelValue', 'grid-config-save'],

  data() {
    return {
      config: {
        rows: 2,
        columns: 3,
        density: 'normal',
        cardDesign: 'standard',
        sortBy: 'name',
        sortOrder: 'asc',
        showBadges: true,
        showTrend: true,
        showPreviousValue: true,
        showTarget: true,
        hoverEffects: true,
        clickToDetails: true
      },
      densityOptions: [
        { value: 'compact', text: 'Kompakt - Mehr Karten auf einen Blick' },
        { value: 'normal', text: 'Normal - Ausgewogene Darstellung' },
        { value: 'comfortable', text: 'Komfortabel - Großzügiger Abstand' }
      ],
      cardDesignOptions: [
        { value: 'standard', text: 'Standard' },
        { value: 'minimal', text: 'Minimal' },
        { value: 'detailed', text: 'Detailliert' }
      ],
      sortByOptions: [
        { value: 'name', text: 'Name' },
        { value: 'value', text: 'Aktueller Wert' },
        { value: 'target', text: 'Zielwert' },
        { value: 'trend', text: 'Trend' },
        { value: 'country', text: 'Land' },
        { value: 'period', text: 'Welle' }
      ],
      sortOrderOptions: [
        { value: 'asc', text: 'Aufsteigend' },
        { value: 'desc', text: 'Absteigend' }
      ]
    };
  },

  computed: {
    isOpen: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },

    totalCards() {
      return this.config.rows * this.config.columns;
    }
  },

  watch: {
    initialConfig: {
      immediate: true,
      handler(newConfig) {
        if (newConfig && Object.keys(newConfig).length > 0) {
          this.config = { ...this.config, ...newConfig };
        }
      }
    }
  },

  methods: {
    getDesignDescription(design) {
      const descriptions = {
        standard: 'Name, Wert, Badges (Land, Welle, Datafreeze), Trend, Vorwelle',
        minimal: 'Nur Name und Wert - Reduzierte Ansicht',
        detailed: 'Alle verfügbaren Informationen inklusive Diagramm'
      };
      return descriptions[design] || '';
    },

    handleSave() {
      this.$emit('grid-config-save', { ...this.config });
      this.isOpen = false;
    },

    handleCancel() {
      this.isOpen = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-icon {
  color: var(--bmw-primary);
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--bmw-primary);
  margin: 0;
}

.modal-description {
  font-size: 14px;
  color: var(--color-muted-foreground);
  margin-bottom: 24px;
}

.config-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-height: calc(90vh - 200px);
  overflow-y: auto;
  padding-right: 8px;
}

.config-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-border);
}

.section-icon {
  color: var(--bmw-primary);
}

.section-title {
  font-size: 16px;
  font-weight: 500;
  color: var(--color-foreground);
  margin: 0;
}

.section-divider {
  margin: 0;
  border-color: var(--color-border);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin: 0;
}

.grid-inputs {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.input-label {
  font-size: 12px;
  color: var(--color-muted-foreground);
  margin: 0;
}

.help-text {
  font-size: 12px;
  color: var(--color-muted-foreground);
  margin: 0;
}

.design-description {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: var(--color-muted-foreground);
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 12px;

  :deep(.form-check) {
    margin-bottom: 0;
  }
}

.switch-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
}

.switch-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.switch-label {
  font-size: 14px;
  min-width: 12rem;
  font-weight: 500;
  color: var(--color-foreground);
  margin: 0;
}

.switch-description {
  font-size: 12px;
  color: var(--color-muted-foreground);
  margin: 0;
}

// Dark Mode
:root.dark,
[data-bs-theme="dark"] {
  .modal-title,
  .section-title,
  .switch-label {
    color: var(--color-foreground);
  }

  .modal-description,
  .help-text,
  .design-description,
  .switch-description,
  .input-label {
    color: var(--color-muted-foreground);
  }

  .section-header {
    border-bottom-color: var(--color-border);
  }

  .section-divider {
    border-color: var(--color-border);
  }

  .switch-item {
    background: var(--color-muted);
  }
}
</style>
